import { useMemo, useState } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';
import { locationIsKia } from '../../../utils/helpers';
import Modal from '../../components/ui/Modal';
import WebComponentSnippet from '../../components/web-component-snippet';

interface Props {
  page: string;
  props?: string;
}

const CDN_URL = process.env.URL || 'https://kia-pages-cdn.vercel.app';

const kebabize = (str: string) =>
  str.replace(
    /[A-Z]+(?![a-z])|[A-Z]/g,
    ($, ofs) => (ofs ? '-' : '') + $.toLowerCase(),
  );

const makeIntoDataProp = (propsString: string = '') => {
  if (!propsString) return '';

  const matches = propsString.match(/([a-zA-ZäöåÄÖÅ-]+)="([^"]*)"/g);
  if (!matches) return '';

  return matches
    .map((e) => {
      const [key, value] = e.split(/=(.+)/);
      return `data-${kebabize(key)}=${value}`;
    })
    .join(' ');
};

export default function Snippet({ page, props }: Props) {
  if (locationIsKia()) return null;

  const [isOpen, setIsOpen] = useState(false);
  const [includeFont, setIncludeFont] = useState(false);
  const [copiedValue, copy] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);

  const fullSnippet = useMemo(() => {
    const js = `<script type="module" src="${CDN_URL}/prod/load.js"></script>`;

    const dataAttributes = makeIntoDataProp(props);
    const fontAttribute = includeFont ? 'data-include-font' : '';

    const html =
      `<div id="kia-page" data-kia-page="${page}" ${dataAttributes} ${fontAttribute}></div>`.replace(
        /\s+/g,
        ' ',
      );

    return `${js}\n${html}`;
  }, [page, props, includeFont]);

  const handleCopy = () => {
    copy(fullSnippet);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 rounded-lg border border-white bg-black px-4 py-2 text-sm font-medium text-white shadow-lg hover:bg-gray-900 transition-colors"
      >
        Visa kodsnutt
      </button>

      {isOpen && (
        <Modal clickOutside={() => setIsOpen(false)}>
          <div className="w-full max-w-lg bg-white p-6 rounded-lg shadow-xl">
            <h2 className="mb-2 text-center text-xl font-bold text-gray-900">
              Kodsnutt
            </h2>
            <p className="mb-6 text-center text-sm ">
              Kopiera koden nedan och klistra in där du vill att sidan ska
              visas.
              <br />
              <span className="text-xs italic">
                Obs: Script-taggen behöver bara laddas en gång per sida.
              </span>
            </p>

            <div className="flex items-center justify-between p-4 rounded-lg border mb-4">
              <div className="flex flex-col">
                <label
                  htmlFor="include-font"
                  className="text-sm font-medium cursor-pointer"
                >
                  Inkludera Kia typsnitt
                </label>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="include-font"
                  checked={includeFont}
                  onChange={(e) => setIncludeFont(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-black/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
              </label>
            </div>

            <div className="relative mb-6 overflow-hidden rounded-lg bg-gray-900 text-white">
              <pre className="max-h-64 overflow-auto p-4 text-xs font-mono leading-relaxed select-all">
                {fullSnippet}
              </pre>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCopy}
                className={`flex-1 rounded-md py-2.5 text-sm font-semibold transition-all ${
                  isCopied
                    ? 'bg-green-500 text-white'
                    : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                {isCopied ? 'Kopierad!' : 'Kopiera kod'}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Stäng
              </button>
            </div>
          </div>
        </Modal>
      )}

      <WebComponentSnippet page={page} props={props} />
    </>
  );
}
